import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
// import { supabase } from '../lib/supabase-client'; // Unused, commented out

// Define the structure of a test item in the catalog
export interface TestCatalogItem {
  id: string;
  name: string;
  description?: string | null;
  preparation?: string | null;
  category?: string | null;
  // Add other relevant fields like cost, turnaround time, etc.
}

// Define the structure returned by the hook
interface UseTestCatalogResult {
  tests: TestCatalogItem[];
  categories: string[];
  isLoading: boolean;
  error: Error | null;
}

// Query Keys
export const testCatalogKeys = {
  all: ['testCatalog'] as const,
  list: () => [...testCatalogKeys.all, 'list'] as const,
};

// Mock Data (replace with actual fetching logic)
const MOCK_TEST_CATALOG: TestCatalogItem[] = [
  { id: 'cbc', name: 'Complete Blood Count', category: 'Blood Tests', description: 'Measures different components of your blood.', preparation: 'None' },
  { id: 'lipid', name: 'Lipid Panel', category: 'Blood Tests', description: 'Measures fats and fatty substances used as a source of energy by your body.', preparation: 'Fasting required' },
  { id: 'tsh', name: 'Thyroid Stimulating Hormone (TSH)', category: 'Blood Tests', description: 'Measures the amount of TSH in your blood.', preparation: 'None' },
  { id: 'xray_chest', name: 'Chest X-Ray', category: 'Imaging', description: 'Uses radiation to create pictures of the structures inside your chest.', preparation: 'Remove jewelry' },
  { id: 'mri_brain', name: 'MRI Brain', category: 'Imaging', description: 'Uses magnetic fields and radio waves to create detailed images of the brain.', preparation: 'Remove metal objects' },
  { id: 'ecg', name: 'Electrocardiogram (ECG)', category: 'Cardiology', description: 'Records the electrical signal from your heart.', preparation: 'None' },
];

// Fetching function (to be implemented)
const fetchTestCatalog = async (): Promise<TestCatalogItem[]> => {
  console.log('Fetching test catalog (using mock data for now)...');
  // TODO: Replace with Supabase query
  // const { data, error } = await supabase.from('test_catalog').select('*');
  // if (error) throw error;
  // return data || [];
  return Promise.resolve(MOCK_TEST_CATALOG); // Return mock data
};

/**
 * Hook to fetch the test catalog data.
 */
export const useTestCatalog = (): UseTestCatalogResult => {
  const { data, isLoading, error } = useQuery<TestCatalogItem[], Error>({
    queryKey: testCatalogKeys.list(),
    queryFn: fetchTestCatalog,
    staleTime: Infinity, // Catalog data is relatively static
  });

  // Extract unique categories from the fetched tests
  const categories = useMemo(() => {
    if (!data) return [];
    const categorySet = new Set<string>();
    data.forEach(test => {
      if (test.category) {
        categorySet.add(test.category);
      }
    });
    return Array.from(categorySet).sort();
  }, [data]);

  return {
    tests: data ?? [],
    categories,
    isLoading,
    error: error instanceof Error ? error : null,
  };
}; 